import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';

import config from '../../config';
import routeConfiguration from '../../routing/routeConfiguration';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { metaTagProps } from '../../util/seo';
import { canonicalRoutePath } from '../../util/routes';
import { CookieConsent } from '../../components';
import css from './Page.module.css';
// Despite the file name, a 500x500 square with the BUYSELL Clothing logo. Kept until new
// 1200x630 artwork arrives (BSC-21).
import defaultShareImage from '../../assets/sneakertimeFacebook-1200x630.png';

const DEFAULT_SHARE_IMAGE_SIZE = 500;

// Share previews only load absolute image URLs; bundled assets are paths on this site
const absoluteUrl = (rootURL, url) =>
  /^https?:\/\//.test(url) ? url : `${rootURL}/${url.replace(/^\//, '')}`;

// Brand logo for the Organization schema: https, crawlable and at least 112x112px
const ORGANIZATION_LOGO_PATH = '/static/icons/android-chrome-512x512.png';

const preventDefault = e => {
  e.preventDefault();
};

const twitterPageURL = siteTwitterHandle => {
  if (siteTwitterHandle && siteTwitterHandle.charAt(0) === '@') {
    return `https://twitter.com/${siteTwitterHandle.substring(1)}`;
  } else if (siteTwitterHandle) {
    return `https://twitter.com/${siteTwitterHandle}`;
  }
  return null;
};

class PageComponent extends Component {
  constructor(props) {
    super(props);
    // Keeping scrollPosition out of state reduces rendering cycles (and no bad states rendered)
    this.scrollPosition = 0;
    this.contentDiv = null;
    this.scrollingDisabledChanged = this.scrollingDisabledChanged.bind(this);
  }

  componentDidMount() {
    // By default a dropped file is loaded in the browser window as a
    // file URL. We want to prevent this since it might loose a lot of
    // data the user has typed but not yet saved. Preventing requires
    // handling both dragover and drop events.
    document.addEventListener('dragover', preventDefault);
    document.addEventListener('drop', preventDefault);

    // Remove duplicated server-side rendered page schema.
    // It's in <body> to improve initial rendering performance,
    // but after web app is initialized, react-helmet-async operates with <head>
    const pageSchema = document.getElementById('page-schema');
    if (pageSchema) {
      pageSchema.remove();
    }
  }

  componentWillUnmount() {
    document.removeEventListener('dragover', preventDefault);
    document.removeEventListener('drop', preventDefault);
  }

  scrollingDisabledChanged(currentScrollingDisabled) {
    if (currentScrollingDisabled && currentScrollingDisabled !== this.scrollingDisabled) {
      // Update current scroll position, if scrolling is disabled (e.g. modal is open)
      this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
      this.scrollingDisabled = currentScrollingDisabled;
    } else if (currentScrollingDisabled !== this.scrollingDisabled) {
      this.scrollingDisabled = currentScrollingDisabled;
    }
  }

  render() {
    const {
      className,
      rootClassName,
      children,
      location,
      intl,
      scrollingDisabled,
      referrer,
      author,
      openGraphType,
      description,
      facebookImages,
      published,
      schema,
      tags,
      title,
      listingDescription,
      twitterHandle,
      twitterImages,
      updated,
      params,
      robots,
      canonicalListingTitle,
    } = this.props;

    const classes = classNames(rootClassName || css.root, className, {
      [css.scrollingDisabled]: scrollingDisabled,
    });

    this.scrollingDisabledChanged(scrollingDisabled);

    const canonicalRootURL = config.canonicalRootURL;
    const shouldReturnPathOnly = referrer && referrer !== 'unsafe-url';
    const canonicalPath = canonicalRoutePath(routeConfiguration(), location, shouldReturnPathOnly, {
      listingTitle: canonicalListingTitle,
    });
    const canonicalUrl = `${canonicalRootURL}${canonicalPath}`;

    const siteTitle = config.siteTitle;
    // A page's own title wins; pages without one get the brand title (BSC-18)
    const pageTitle = title || intl.formatMessage({ id: 'Page.metaTitle' }, { siteTitle });
    const schemaDescription = intl.formatMessage({ id: 'Page.metaDescription' });
    const pageDescription = description || listingDescription || schemaDescription;

    // Listing pages share their first photo; other pages share the default image (BSC-21)
    const listingFirstImage = schema?.image?.[0];
    const shareImage = listingFirstImage
      ? { url: listingFirstImage }
      : {
          url: absoluteUrl(canonicalRootURL, defaultShareImage),
          width: DEFAULT_SHARE_IMAGE_SIZE,
          height: DEFAULT_SHARE_IMAGE_SIZE,
        };
    const openGraphFallbackImages = [{ name: 'facebook', ...shareImage }];
    const twitterFallbackImages = [{ name: 'twitter', ...shareImage }];

    const metaToHead = metaTagProps({
      author,
      openGraphType,
      socialSharingTitle: pageTitle,
      socialSharingDescription: pageDescription,
      description: pageDescription,
      facebookImages: openGraphFallbackImages,
      twitterImages: twitterFallbackImages,
      published,
      tags,
      twitterHandle,
      updated,
      url: canonicalUrl,
      locale: intl.locale,
    });

    const facebookPage = config.siteFacebookPage;
    const twitterPage = twitterPageURL(config.siteTwitterHandle);
    // Only the profiles the PM confirmed: Instagram stays in the footer but out of search data
    // until it is confirmed too (BSC-23)
    const sameOrganizationAs = [facebookPage, twitterPage].filter(v => v != null);

    // Schema for search engines (helps them to understand what this page is about)
    // http://schema.org
    // We are using JSON-LD format

    // Schema attribute can be either single schema object or an array of objects
    // This makes it possible to include several different items from the same page.
    // E.g. Product, Place, Video
    const hasSchema = schema != null;
    const schemaFromProps = hasSchema && Array.isArray(schema) ? schema : hasSchema ? [schema] : [];
    const schemaArrayJSONString = JSON.stringify([
      ...schemaFromProps,
      {
        '@context': 'http://schema.org',
        '@type': 'Organization',
        '@id': `${canonicalRootURL}#organization`,
        logo: `${canonicalRootURL}${ORGANIZATION_LOGO_PATH}`,
        name: siteTitle,
        alternateName: config.siteAlternateName,
        sameAs: sameOrganizationAs,
        url: canonicalRootURL,
      },
      // No SearchAction: search URLs with parameters are noindexed (BSC-14), so search
      // results pages are not offered to search engines as an entry point (BSC-20).
      {
        '@context': 'http://schema.org',
        '@type': 'WebSite',
        '@id': `${canonicalRootURL}#website`,
        description: schemaDescription,
        name: siteTitle,
        alternateName: config.siteAlternateName,
        url: canonicalRootURL,
        publisher: { '@id': `${canonicalRootURL}#organization` },
      },
    ]);

    const scrollPositionStyles = scrollingDisabled
      ? { marginTop: `${-1 * this.scrollPosition}px` }
      : {};

    // If scrolling is not disabled, but content element has still scrollPosition set
    // in style attribute, we scrollTo scrollPosition.
    const hasMarginTopStyle = this.contentDiv && this.contentDiv.style.marginTop;
    if (!scrollingDisabled && hasMarginTopStyle) {
      window.requestAnimationFrame(() => {
        window.scrollTo(0, this.scrollPosition);
      });
    }

    return (
      <div className={classes}>
        <Helmet
          htmlAttributes={{
            lang: intl.locale,
          }}
        >
          <title>{pageTitle}</title>
          {referrer ? <meta name="referrer" content={referrer} /> : null}
          <link rel="canonical" href={canonicalUrl} />
          {robots ? <meta name="robots" content={robots} /> : null}
          <meta httpEquiv="Content-Type" content="text/html; charset=UTF-8" />
          <meta httpEquiv="Content-Language" content={intl.locale} />
          {metaToHead.map((metaProps, i) => (
            <meta key={i} {...metaProps} />
          ))}
          <script id="page-schema" type="application/ld+json">
            {schemaArrayJSONString.replace(/</g, '\\u003c')}
          </script>
        </Helmet>
        <CookieConsent />
        <div
          className={css.content}
          style={scrollPositionStyles}
          ref={c => {
            this.contentDiv = c;
          }}
        >
          {children}
        </div>
      </div>
    );
  }
}

const { any, array, arrayOf, bool, func, number, object, oneOfType, shape, string } = PropTypes;

PageComponent.defaultProps = {
  className: null,
  rootClassName: null,
  children: null,
  author: null,
  openGraphType: 'website',
  description: null,
  facebookImages: null,
  twitterImages: null,
  published: null,
  referrer: null,
  robots: null,
  canonicalListingTitle: null,
  schema: null,
  socialSharing: null,
  tags: null,
  twitterHandle: null,
  updated: null,
};

PageComponent.propTypes = {
  className: string,
  rootClassName: string,
  children: any,
  scrollingDisabled: bool.isRequired,

  // Handle referrer policy
  referrer: string,

  // robots meta tag content, e.g. 'noindex, follow'. Not rendered when empty.
  robots: string,

  // Listing title, used to build the /l/{slug}/{id} canonical URL on listing pages
  canonicalListingTitle: string,

  // SEO related props
  author: string,
  openGraphType: string, // og:type
  description: string, // page description
  facebookImages: arrayOf(
    shape({
      width: number.isRequired,
      height: number.isRequired,
      url: string.isRequired,
    })
  ),
  twitterImages: arrayOf(
    shape({
      width: number.isRequired,
      height: number.isRequired,
      url: string.isRequired,
    })
  ),
  published: string, // article:published_time
  schema: oneOfType([object, array]), // http://schema.org
  socialSharing: shape({
    title: string,
    description: string,
    images1200: arrayOf(
      // Page asset file can define this
      shape({
        width: number.isRequired,
        height: number.isRequired,
        url: string.isRequired,
      })
    ),
    images600: arrayOf(
      // Page asset file can define this
      shape({
        width: number.isRequired,
        height: number.isRequired,
        url: string.isRequired,
      })
    ),
  }),
  tags: string, // article:tag
  title: string, // page title
  twitterHandle: string, // twitter handle
  updated: string, // article:modified_time

  // from withRouter
  history: shape({
    listen: func.isRequired,
  }).isRequired,
  location: object.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const Page = injectIntl(withRouter(PageComponent));
Page.displayName = 'Page';

export default Page;
